import { useCallback, useEffect, useMemo, useState } from 'react';
import { ExecutionAlert } from '../components/executions/DuplicateExecutionNotice';
import { trackerAiClient as api } from '@/app/TrackerAi/api/trackerAiClient';
import { SettingsPageSkeleton } from '../components/common/Skeleton';
import { CompetitorModal } from '../components/Modals';
import { NoProject } from '../components/NoProject';
import { BrandIdentitySection } from '../components/settings/BrandIdentitySection';
import { DangerZoneSection } from '../components/settings/DangerZoneSection';
import { SettingsCompetitorsPreview } from '../components/settings/SettingsCompetitorsPreview';
import { SettingsModelSection } from '../components/settings/SettingsModelSection';
import { SettingsTopicsSection } from '../components/settings/SettingsTopicsSection';
import { useProject } from '../context/ProjectContext';
import type { CompetitorInsight } from '../types';

export function SettingsPage() {
  return (
    <NoProject>
      <SettingsContent />
    </NoProject>
  );
}

function SettingsContent() {
  const {
    projectId,
    project,
    data,
    loading: projectLoading,
    refresh,
    userTopics,
    topicsLoaded,
    models,
    defaultModelId,
    setDefaultModelId,
    error,
    setError,
    setNewTopicOpen,
    deleteProject,
    deletingProject
  } = useProject();

  const [loading, setLoading] = useState(false);
  const [competitors, setCompetitors] = useState<CompetitorInsight[]>([]);
  const [competitorsLoaded, setCompetitorsLoaded] = useState(false);
  const [competitorModalOpen, setCompetitorModalOpen] = useState(false);
  const [editingCompetitor, setEditingCompetitor] = useState<CompetitorInsight | null>(null);
  const [savingCompetitor, setSavingCompetitor] = useState(false);
  const [deletingAliasId, setDeletingAliasId] = useState<string | null>(null);
  const [updatingDomainId, setUpdatingDomainId] = useState<string | null>(null);
  const [updatingAliasId, setUpdatingAliasId] = useState<string | null>(null);
  const [renamingTopicId, setRenamingTopicId] = useState<string | null>(null);

  const insight = data?.insight;

  const loadCompetitors = useCallback(async () => {
    if (!projectId) return;
    const list = await api.getCompetitors(projectId);
    setCompetitors(list);
  }, [projectId]);

  useEffect(() => {
    let cancelled = false;
    setCompetitorsLoaded(false);
    loadCompetitors()
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setCompetitorsLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [loadCompetitors]);

  const topicsWithCounts = useMemo(() => {
    const promptCounts = new Map(
      (insight?.topics ?? []).map(topic => [topic.id, topic.counts.total])
    );
    return userTopics.map(topic => ({
      ...topic,
      promptCount: promptCounts.get(topic.id)
    }));
  }, [userTopics, insight?.topics]);

  async function addDomain(host: string) {
    if (!projectId) return;
    setLoading(true);
    setError('');
    try {
      await api.createDomain(projectId, {
        host,
        isPrimary: (insight?.domains.length ?? 0) === 0,
        isTrackingSource: true
      });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  async function addAlias(name: string) {
    if (!projectId) return;
    setLoading(true);
    setError('');
    try {
      await api.createBrandAlias(projectId, {
        name,
        isPrimary: insight?.brandAliases.length === 0
      });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  async function handleDeleteAlias(aliasId: string) {
    if (!projectId) return;
    setDeletingAliasId(aliasId);
    setError('');
    try {
      await api.deleteBrandAlias(projectId, aliasId);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDeletingAliasId(null);
    }
  }

  async function handleUpdateDomain(domainId: string, host: string) {
    if (!projectId) return;
    setUpdatingDomainId(domainId);
    setError('');
    try {
      await api.updateDomain(projectId, domainId, { host });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      throw e;
    } finally {
      setUpdatingDomainId(null);
    }
  }

  async function handleUpdateAlias(aliasId: string, name: string) {
    if (!projectId) return;
    setUpdatingAliasId(aliasId);
    setError('');
    try {
      await api.updateBrandAlias(projectId, aliasId, { name });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      throw e;
    } finally {
      setUpdatingAliasId(null);
    }
  }

  async function handleSaveCompetitor(body: {
    brandName: string;
    websiteUrl: string;
    aliases: string[];
  }) {
    if (!projectId) return;
    setSavingCompetitor(true);
    setError('');
    try {
      if (editingCompetitor) {
        await api.updateCompetitor(projectId, editingCompetitor.id, body);
      } else {
        await api.createCompetitor(projectId, body);
      }
      setEditingCompetitor(null);
      await loadCompetitors();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      throw e;
    } finally {
      setSavingCompetitor(false);
    }
  }

  function openAddCompetitor() {
    setEditingCompetitor(null);
    setCompetitorModalOpen(true);
  }

  function openEditCompetitor(competitor: CompetitorInsight) {
    setEditingCompetitor(competitor);
    setCompetitorModalOpen(true);
  }

  async function handleRenameTopic(topicId: string, name: string) {
    if (!projectId) return;
    setRenamingTopicId(topicId);
    setError('');
    try {
      await api.updateTopic(projectId, topicId, { name });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      throw e;
    } finally {
      setRenamingTopicId(null);
    }
  }

  async function handleDeleteProject() {
    if (!projectId || !project) return;
    if (
      !confirm(
        `پروژه «${project.domain}» و همه تاپیک‌ها، پرامپت‌ها، دامنه‌ها و رقبای آن حذف شود؟ این عمل قابل بازگشت نیست.`
      )
    ) {
      return;
    }
    try {
      await deleteProject(projectId);
    } catch {
      // error shown via context
    }
  }

  return (
    <div>
      <div className="mx-auto">
        <header className="mb-4">
          <h1 className="fs-3 fw-semibold text-1000 mb-1">تنظیمات</h1>
          <p className="fs--1 text-700 mb-0">
            {project
              ? `پیش‌فرض‌ها و میانبرهای پروژه ${project.domain}.`
              : 'پروژه خود را پیکربندی کنید.'}
          </p>
          {insight && (
            <div className="mt-3 d-flex flex-wrap gap-2">
              <StatPill label="دامنه‌ها" value={insight.counts.domains} />
              <StatPill label="رقبا" value={competitors.length} />
              <StatPill label="تاپیک‌ها" value={insight.counts.topics} />
              <StatPill label="پرامپت‌ها" value={insight.counts.prompts} />
            </div>
          )}
        </header>

        {projectLoading && !data ? (
          <SettingsPageSkeleton />
        ) : (
          <>
            {error && <ExecutionAlert message={error} className="mb-4" />}

            {insight && (
              <div className="d-flex flex-column gap-4">
                <BrandIdentitySection
                  domains={insight.domains}
                  aliases={insight.brandAliases}
                  loading={loading}
                  onAddDomain={addDomain}
                  onAddAlias={addAlias}
                  onUpdateDomain={handleUpdateDomain}
                  onUpdateAlias={handleUpdateAlias}
                  onDeleteAlias={handleDeleteAlias}
                  updatingDomainId={updatingDomainId}
                  updatingAliasId={updatingAliasId}
                  deletingAliasId={deletingAliasId}
                />

                <SettingsCompetitorsPreview
                  competitors={competitors}
                  loading={!competitorsLoaded}
                  onAdd={openAddCompetitor}
                  onEdit={openEditCompetitor}
                />

                <SettingsTopicsSection
                  topics={topicsWithCounts}
                  loading={!topicsLoaded}
                  onNewTopic={() => setNewTopicOpen(true)}
                  onRename={handleRenameTopic}
                  renamingTopicId={renamingTopicId}
                />

                {models.length > 0 && (
                  <SettingsModelSection
                    models={models}
                    value={defaultModelId}
                    onChange={setDefaultModelId}
                  />
                )}

                <DangerZoneSection
                  projectName={project?.domain ?? 'این پروژه'}
                  deleting={deletingProject}
                  onDelete={() => void handleDeleteProject()}
                />
              </div>
            )}
          </>
        )}
      </div>

      <CompetitorModal
        open={competitorModalOpen}
        onClose={() => {
          setCompetitorModalOpen(false);
          setEditingCompetitor(null);
        }}
        initial={
          editingCompetitor
            ? {
                brandName: editingCompetitor.brandName,
                websiteUrl: editingCompetitor.websiteUrl,
                aliases: editingCompetitor.aliases.map(alias => alias.name)
              }
            : undefined
        }
        title={editingCompetitor ? 'ویرایش رقیب' : 'افزودن رقیب'}
        onSubmit={handleSaveCompetitor}
        saving={savingCompetitor}
      />
    </div>
  );
}

function StatPill({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-3 bg-100 dark__bg-1100 border px-3 py-1 fs--1">
      <span className="text-700">{label}</span>{' '}
      <span
        className="fw-semibold text-1000"
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {value}
      </span>
    </div>
  );
}

export default SettingsPage;
